import { Circle } from "@mui/icons-material";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";
import theme from "../../App/Styles/theme";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";
import { IPerformanceReport } from "../../App/Typings/Reports/CampaignEmailPerformanceReport";
import { customLegendStyles } from "../CandidatesAddedByCampaign/CandidatesAddedByCampaignReport";
import { DiagonalXAxisTick } from "../Components/DiagonalXAxisTick";
import ReportContainer from "../Components/ReportContainer";

export const lightLineColor = "#CD90C9";
export const darkLineColor = "#6F366B";

const generateCustomLegendPayload = (
  reportData?: IPerformanceReport,
  enableEmailsDeliveredAnalytics?: boolean,
) => {
  let totalDeliveredCount = 0;
  let totalBouncesCount = 0;
  let totalReplyCount = 0;
  let totalUnsubscribesCount = 0;

  for (const day of reportData?.dataPoints ?? []) {
    totalDeliveredCount += day.deliveredCount;
    totalBouncesCount += day.bounceCount;
    totalReplyCount += day.replyCount;
    totalUnsubscribesCount += day.unsubscribeCount;
  }

  const payload = [
    {
      dataKey: "replyCount",
      color: darkLineColor,
      value: "Total Text Replies",
      totalCount: totalReplyCount,
    },
    {
      dataKey: "bounceCount",
      color: theme.palette.motivatePrimary.main,
      value: "Bounces",
      totalCount: totalBouncesCount,
    },
    {
      dataKey: "unsubscribeCount",
      color: lightLineColor,
      value: "Unsubscribes",
      totalCount: totalUnsubscribesCount,
    },
  ];

  // conditionally add the "Delivered" data if the feature toggle is enabled
  if (enableEmailsDeliveredAnalytics) {
    payload.unshift({
      dataKey: "deliveredCount",
      color: theme.palette.captivatePrimary.main, // update the color
      value: "Delivered",
      totalCount: totalDeliveredCount,
    });
  }

  return payload;
};

const CustomLegend = (props: any) => {
  return (
    <Stack direction="row" alignItems="center" columnGap={2} rowGap={1} flexWrap="wrap">
      {props.payload.map((entry: any, index: number) => {
        return (
          <Box
            id={`campaigntextperformance-legenditem-${index}`}
            key={`campaigntextperformance-legenditem-${index}`}
          >
            <Stack title={entry.value} direction="row" alignItems="center" gap={0.5}>
              <Circle sx={{ color: entry.color }} fontSize="small" />
              <Typography color={entry.color} key={`item-${index}`} fontWeight="bold" sx={customLegendStyles}>
                {entry.value}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const CustomToolTip = (props: any) => {
  const { enableEmailsDeliveredAnalytics } = useFlags<IFeatureToggles>();
  const { active, payload, id } = props;

  if (!active || !payload?.length) {
    return null;
  }

  const dateAdded = payload[0].payload.dateAdded;
  const deliveredCount = payload.find((entry: any) => entry.dataKey === "deliveredCount")?.value ?? 0;
  const bounceCount = payload.find((entry: any) => entry.dataKey === "bounceCount")?.value ?? 0;
  const replyCount = payload.find((entry: any) => entry.dataKey === "replyCount")?.value ?? 0;
  const unsubscribeCount = payload.find((entry: any) => entry.dataKey === "unsubscribeCount")?.value ?? 0;

  return (
    <Card>
      <CardContent
        sx={{
          ".MuiTypography-root": { textAlign: "left", lineHeight: 1, pb: 1 },
          "&:last-child": { py: 1 },
        }}
      >
        {enableEmailsDeliveredAnalytics && (
          <Typography
            sx={{ fontWeight: "normal" }}
            data-test={`${id}textperformance-tooltip-delivered-${dateAdded}`}
          >
            Delivered: <span style={{ fontWeight: "bold" }}>{deliveredCount}</span>
          </Typography>
        )}
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}textperformance-tooltip-unsubscribes-${dateAdded}`}
        >
          Unsubscribes: <span style={{ fontWeight: "bold" }}>{unsubscribeCount}</span>
        </Typography>
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}textperformance-tooltip-total-text-replies-${dateAdded}`}
        >
          Total Text Replies: <span style={{ fontWeight: "bold" }}>{replyCount}</span>
        </Typography>
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}textperformance-tooltip-bounces-${dateAdded}`}
        >
          Bounces: <span style={{ fontWeight: "bold" }}>{bounceCount}</span>
        </Typography>
        <Typography fontWeight={400} color={(theme) => theme.palette.text.secondary} sx={{ mb: 0, mt: 1 }}>
          {dateAdded}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const TextPerformanceReport = ({
  id,
  reportData,
  enabled,
  loading,
}: {
  id: string;
  reportData: IPerformanceReport | undefined;
  enabled: boolean;
  loading: boolean;
}) => {
  const { enableEmailsDeliveredAnalytics } = useFlags<IFeatureToggles>();

  if (!enabled) {
    return null;
  }

  return (
    <ReportContainer title="Text Performance Per Day" id={`${id}-text-performance`} isLoading={loading}>
      <AreaChart
        data={reportData?.dataPoints}
        style={{
          position: "relative",
        }}
        margin={{
          top: 0,
          right: 30,
          left: 20,
          bottom: 60,
        }}
      >
        <defs>
          {enableEmailsDeliveredAnalytics && (
            <linearGradient id="area-gradient-delivered" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.palette.captivatePrimary.main} stopOpacity={0.3} />
              <stop offset="95%" stopColor={theme.palette.captivatePrimary.main} stopOpacity={0} />
            </linearGradient>
          )}
          <linearGradient id="area-gradient-bounces" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.palette.motivatePrimary.main} stopOpacity={0.3} />
            <stop offset="95%" stopColor={theme.palette.motivatePrimary.main} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="area-gradient-replies" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={darkLineColor} stopOpacity={0.3} />
            <stop offset="95%" stopColor={darkLineColor} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="area-gradient-unsubs" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lightLineColor} stopOpacity={0.3} />
            <stop offset="95%" stopColor={lightLineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#eeeded" vertical={false} />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey="dateAdded"
          tick={<DiagonalXAxisTick />}
          tickFormatter={(value: any) => new Date(value).toLocaleDateString()}
        />
        <YAxis axisLine={false} tickLine={false} type="number" domain={["auto", "dataMax"]} />
        <Tooltip
          content={<CustomToolTip id={id} />}
          cursor={{ stroke: theme.palette.motivatePrimary.main, strokeDasharray: 5 }}
        />
        <Legend
          layout="horizontal"
          verticalAlign="top"
          align="center"
          content={<CustomLegend />}
          payload={generateCustomLegendPayload(reportData, enableEmailsDeliveredAnalytics)}
          wrapperStyle={{ paddingBottom: 30 }}
        />
        <Area
          type="monotone"
          dataKey="unsubscribeCount"
          stroke={lightLineColor}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-unsubs)"
          activeDot={{ r: 6 }}
        />
        <Area
          type="monotone"
          dataKey="replyCount"
          stroke={darkLineColor}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-replies)"
          activeDot={{ r: 6 }}
        />
        <Area
          type="monotone"
          dataKey="bounceCount"
          stroke={theme.palette.motivatePrimary.main}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-bounces)"
          activeDot={{ r: 6 }}
        />
        {enableEmailsDeliveredAnalytics && (
          <Area
            type="monotone"
            dataKey="deliveredCount"
            stroke={theme.palette.captivatePrimary.main}
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#area-gradient-delivered)"
            activeDot={{ r: 6 }}
          />
        )}
      </AreaChart>
    </ReportContainer>
  );
};
